'use client';

import { X } from 'lucide-react';
import { SupportIllustration } from './SupportIllustration';
import { NewRequestForm } from './NewRequestForm';
import { MyRequests } from './MyRequests';
import { Thread } from './Thread';
import { AssistantTab } from './AssistantTab';
import { useSupportPanel } from './SupportProvider';

/**
 * Le panneau du support (P02), ancre en bas a droite. Il ne bloque pas la page : on continue a travailler derriere.
 * Ferme, il reste monte (cache), si bien que le brouillon et un envoi en cours survivent.
 */
export function SupportPanel() {
  const { isOpen, close, tab, setTab, thread, setThread, unread, capturing, assistant } = useSupportPanel();
  const tabClass = (on: boolean) => `flex-1 rounded-[7px] px-2 py-1.5 text-xs font-semibold ${on ? 'bg-[var(--paper)] text-[var(--ink-900)] shadow-sm' : 'text-[var(--ink-500)] hover:text-[var(--ink-800)]'}`;
  return (
    <section
      hidden={!isOpen}
      aria-label="Besoin d'aide ?"
      data-support="panel"
      data-shots-scope
      // Masque (sans changer la mise en page) le temps d'une capture
      style={capturing ? { visibility: 'hidden' } : undefined}
      // Les touches tapees ici ne declenchent pas les raccourcis de l'editeur (visite comprise)
      onKeyDown={e => e.stopPropagation()}
      className="fixed bottom-4 right-5 z-[1000] flex max-h-[calc(100vh-32px)] w-[392px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-2xl border border-[var(--ink-200)] bg-[var(--paper)] text-[13px] text-[var(--ink-800)] shadow-2xl"
    >
      <header className="flex items-center gap-2.5 border-b border-[var(--ink-100)] py-3 pl-4 pr-3">
        <span className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full bg-[var(--accent-50)]">
          <SupportIllustration size={28} />
        </span>
        <div className="min-w-0">
          <b className="block text-sm text-[var(--ink-900)]">Besoin d&apos;aide ?</b>
          <span className="text-[11.5px] text-[var(--ink-500)]">{assistant ? 'L\'assistant répond tout de suite, le support sous 24 h.' : 'Nous répondons en général sous 24 h, par mail.'}</span>
        </div>
        <button type="button" onClick={close} title="Fermer" className="af-iconbtn ml-auto">
          <X size={17} />
        </button>
      </header>
      <div role="tablist" className="mx-3.5 mt-2.5 flex gap-0.5 rounded-[10px] border border-[var(--ink-200)] bg-[var(--ink-100)] p-[3px]">
        {/* A2 : l'assistant d'abord, s'il est offert (en developpement seulement, A1) */}
        {assistant && (
          <button type="button" role="tab" aria-selected={tab === 'assistant'} onClick={() => setTab('assistant')} className={tabClass(tab === 'assistant')}>
            Assistant
          </button>
        )}
        <button type="button" role="tab" aria-selected={tab === 'new'} onClick={() => setTab('new')} className={tabClass(tab === 'new')}>
          Nouvelle demande
        </button>
        <button type="button" role="tab" aria-selected={tab === 'list'} onClick={() => { setTab('list'); setThread(null); }} className={tabClass(tab === 'list')}>
          Mes demandes
          {unread > 0 && <span className="ml-1 inline-grid h-4 min-w-4 place-items-center rounded-full bg-[var(--danger)] px-1 text-[10px] text-white">{unread}</span>}
        </button>
      </div>
      {tab === 'assistant' && assistant ? <AssistantTab /> : tab === 'new' || tab === 'assistant' ? <NewRequestForm /> : thread !== null ? <Thread key={thread} number={thread} /> : <MyRequests />}
    </section>
  );
}
